/**
 * The content sync engine's per-note state in the local database (PLAN.md
 * "Client engine"), and the sync mode (D8).
 *
 * Columns next to `content`: `rev`, `base_content` (the base), `base_updated_at`,
 * `inflight_client_id` / `inflight_seq` / `inflight_base_rev` / `inflight_ops`,
 * and `pending_ops` (the buffer). `pending_ops IS NULL` means the note is
 * outside the engine: legacy mode, or a note whose `create` is still queued.
 *
 * Invariant: content === apply(apply(base, inflight?.ops ?? identity), buffer).
 *
 * `recordLocalEdit` is the one choke point every local text change goes
 * through (caller rule 1): `db.ts` `updateNote` and `putNote` call it inside
 * their transaction, before the new content is written. It lives here rather
 * than in `services/syncStorage.ts` because that file imports `db.ts`, and the
 * choke point has to be under the writers, not beside them. A note with an
 * open socket is kicked from here too, so a write that never went through the
 * editor (a fold, a history restore, a tag rename, an image swap) still
 * reaches the server while the HTTP driver leaves the note to the socket.
 *
 * Imports only `sqlite.ts` (and `services/liveNote.ts` and `inflightRecords.ts`,
 * which import nothing of the app's storage), so `db.ts` can use it without a
 * cycle.
 */

import {
  apply,
  checkSyncState,
  diff,
  identityOp,
  isIdentityOp,
  isWellFormedOp,
  localEdit,
  type NoteSyncState,
  type TextOp,
} from "@caliu/utils";
import { clearInflightRecord } from "./inflightRecords";
import { Sentry } from "./sentry";
import { execute, queryOne } from "./sqlite";
import { kickLiveNote } from "./services/liveNote";

export type SyncMode = "legacy" | "v2";

const MODE_KEY = "collabSyncMode";
const LIVE_KEY = "collabLive";
/** Fired on `window` when the pull's `collabFeatures.live` changes. */
export const LIVE_FEATURE_EVENT = "caliu:collab-live";
const CATCH_UP_KEY = "contentCatchUp";

export function getSyncMode(): SyncMode {
  const row = queryOne<{ value: string }>("SELECT value FROM meta WHERE key = ?", [MODE_KEY]);
  return row?.value === "v2" ? "v2" : "legacy";
}

/** Fired on `window` when the sync mode changes. */
export const SYNC_MODE_EVENT = "caliu:sync-mode";

export function setSyncMode(mode: SyncMode): void {
  execute("INSERT OR REPLACE INTO meta (key, value) VALUES (?, ?)", [MODE_KEY, mode]);
  if (typeof window !== "undefined") window.dispatchEvent(new Event(SYNC_MODE_EVENT));
}

/**
 * Whether this account may not change the text of a note it holds with
 * `role`: a viewer never, a member (editor too) while this device syncs in
 * legacy mode, where the server refuses a member's push.
 */
export function memberTextLocked(role: string | null | undefined): boolean {
  if (role === "viewer") return true;
  return role === "editor" && getSyncMode() === "legacy";
}

/** The pull's `collabFeatures.live` (PLAN.md "Live socket"): the open note gets a socket. */
export function getLiveFeature(): boolean {
  return queryOne<{ value: string }>("SELECT value FROM meta WHERE key = ?", [LIVE_KEY])?.value === "1";
}

export function setLiveFeature(on: boolean): void {
  if (getLiveFeature() === on) return;
  execute("INSERT OR REPLACE INTO meta (key, value) VALUES (?, ?)", [LIVE_KEY, on ? "1" : "0"]);
  if (typeof window !== "undefined") window.dispatchEvent(new Event(LIVE_FEATURE_EVENT));
}

const SHARING_KEY = "collabSharing";
/** Fired on `window` when the pull's `collabFeatures.sharing` changes. */
export const SHARING_FEATURE_EVENT = "caliu:collab-sharing";

/**
 * The pull's `collabFeatures.sharing` (PLAN.md D8): notes shared with people
 * arrive as rows of `notes`. Off, the old public-link "Shared with me"
 * (`shared_note_refs`) is still what this account has.
 */
export function getSharingFeature(): boolean {
  return queryOne<{ value: string }>("SELECT value FROM meta WHERE key = ?", [SHARING_KEY])?.value === "1";
}

export function setSharingFeature(on: boolean): void {
  if (getSharingFeature() === on) return;
  execute("INSERT OR REPLACE INTO meta (key, value) VALUES (?, ?)", [SHARING_KEY, on ? "1" : "0"]);
  if (typeof window !== "undefined") window.dispatchEvent(new Event(SHARING_FEATURE_EVENT));
}

/** Notes a pull said changed on the server: the next content pass catches them up. */
export function getCatchUpIds(): Set<string> {
  const row = queryOne<{ value: string }>("SELECT value FROM meta WHERE key = ?", [CATCH_UP_KEY]);
  if (!row?.value) return new Set();
  try {
    const ids = JSON.parse(row.value) as unknown;
    return new Set(Array.isArray(ids) ? ids.filter((x): x is string => typeof x === "string") : []);
  } catch {
    return new Set();
  }
}

export function setCatchUpIds(ids: Set<string>): void {
  execute("INSERT OR REPLACE INTO meta (key, value) VALUES (?, ?)", [CATCH_UP_KEY, JSON.stringify([...ids])]);
}

const UPGRADE_DEFERRALS_KEY = "collabUpgradeDeferrals";

/** Pulls that wanted v2 while legacy note work was left (`upgradeDecision`), per device. */
export function getUpgradeDeferrals(): number {
  const n = Number(queryOne<{ value: string }>("SELECT value FROM meta WHERE key = ?", [UPGRADE_DEFERRALS_KEY])?.value);
  return Number.isInteger(n) && n > 0 ? n : 0;
}

export function setUpgradeDeferrals(n: number): void {
  execute("INSERT OR REPLACE INTO meta (key, value) VALUES (?, ?)", [UPGRADE_DEFERRALS_KEY, String(n)]);
}

export interface EngineRow {
  id: string;
  rev: number;
  base_content: string | null;
  base_updated_at: string;
  content: string;
  title: string;
  inflight_client_id: string | null;
  inflight_seq: number | null;
  inflight_base_rev: number | null;
  inflight_ops: string | null;
  pending_ops: string | null;
  sync_status: string;
  owner_id: string | null;
}

export const ENGINE_ROW_COLUMNS =
  "id, rev, base_content, base_updated_at, content, title, inflight_client_id, inflight_seq, inflight_base_rev, inflight_ops, pending_ops, sync_status, owner_id";

function parseOp(json: string | null): TextOp | null {
  if (json === null) return null;
  try {
    const op = JSON.parse(json) as unknown;
    return isWellFormedOp(op) ? op : null;
  } catch {
    return null;
  }
}

/** The row's state as stored, or null outside the engine. Not checked. */
export function rowToSyncState(row: EngineRow): NoteSyncState | null {
  if (row.pending_ops === null) return null;
  const inflightOps = parseOp(row.inflight_ops);
  const inflight =
    row.inflight_client_id !== null && row.inflight_seq !== null && row.inflight_base_rev !== null && inflightOps
      ? { clientId: row.inflight_client_id, seq: row.inflight_seq, baseRev: row.inflight_base_rev, ops: inflightOps }
      : null;
  return {
    rev: row.rev,
    base: row.base_content,
    baseUpdatedAt: row.base_updated_at,
    content: row.content,
    inflight,
    buffer: parseOp(row.pending_ops) ?? identityOp(row.content.length),
  };
}

export function readEngineRow(noteId: string): EngineRow | null {
  return queryOne<EngineRow>(`SELECT ${ENGINE_ROW_COLUMNS} FROM notes WHERE id = ?`, [noteId]);
}

/**
 * The invariant, checked; when broken, repaired with
 * `buffer = diff(base∘inflight, content)` and reported to Sentry. A broken
 * in-flight change (its ops no longer fit the base) is folded away: the
 * catch-up that follows rebases whatever the server holds.
 */
export function checkedSyncState(s: NoteSyncState, noteId: string): { state: NoteSyncState; repaired: boolean } {
  try {
    checkSyncState(s);
    return { state: s, repaired: false };
  } catch (err) {
    Sentry.captureException(err, { tags: { operation: "note_sync_invariant" }, extra: { noteId } });
    if (s.base === null) return { state: { ...s, inflight: null, buffer: identityOp(s.content.length) }, repaired: true };
    let mid = s.base;
    let inflight = s.inflight;
    try {
      if (inflight) mid = apply(s.base, inflight.ops);
    } catch {
      inflight = null;
      mid = s.base;
    }
    return { state: { ...s, inflight, buffer: diff(mid, s.content) }, repaired: true };
  }
}

/** The note's state, checked (and repaired), or null outside the engine. */
export function loadSyncState(noteId: string): NoteSyncState | null {
  const row = readEngineRow(noteId);
  if (!row) return null;
  const s = rowToSyncState(row);
  if (!s) return null;
  const { state, repaired } = checkedSyncState(s, noteId);
  if (repaired) {
    writeSyncState(noteId, state);
    // The repair dropped the change in flight: its record goes.
    if (s.inflight && !state.inflight) clearInflightRecord(noteId, s.inflight.clientId);
  }
  return state;
}

/**
 * Writes the engine columns (not `content`: the caller writes the text, in
 * the same transaction, when it changed).
 */
export function writeSyncState(noteId: string, s: NoteSyncState): void {
  execute(
    `UPDATE notes SET rev = ?, base_content = ?, base_updated_at = ?,
       inflight_client_id = ?, inflight_seq = ?, inflight_base_rev = ?, inflight_ops = ?, pending_ops = ?
     WHERE id = ?`,
    [
      s.rev,
      s.base,
      s.baseUpdatedAt,
      s.inflight?.clientId ?? null,
      s.inflight?.seq ?? null,
      s.inflight?.baseRev ?? null,
      s.inflight ? JSON.stringify(s.inflight.ops) : null,
      JSON.stringify(s.buffer),
      noteId,
    ],
  );
}

/**
 * Whether the state holds text the server has not confirmed. `syncStatus` is
 * the row's: without a common base the buffer is an identity, yet text of a
 * note not marked synced is still unsent (`hasUnsentText` in @caliu/utils).
 */
export function hasUnsentWork(s: NoteSyncState, syncStatus: string): boolean {
  if (s.inflight !== null || !isIdentityOp(s.buffer)) return true;
  return s.base === null && s.content !== "" && syncStatus !== "synced";
}

/**
 * A note shared with this account as a viewer (PLAN.md D5), or as any member
 * in legacy mode (`memberTextLocked`): it takes no local text edit, whatever
 * the screen allowed. True (and reported: the UI should have stopped it) when
 * `noteId` is such a note.
 */
export function refusesLocalEdit(noteId: string): boolean {
  const row = queryOne<{ share_role: string | null }>("SELECT share_role FROM notes WHERE id = ?", [noteId]);
  if (!memberTextLocked(row?.share_role)) return false;
  Sentry.captureException(new Error("local edit refused on a view-only note"), { extra: { noteId } });
  return true;
}

/**
 * Caller rule 1: a local text change of a note in the engine goes through
 * `localEdit`. Call inside the writer's transaction, before it writes
 * `content`. Does nothing in legacy mode or outside the engine. False when
 * the edit is refused (a viewer's note): the caller must not write the text.
 */
export function recordLocalEdit(noteId: string, text: string): boolean {
  if (getSyncMode() !== "v2") return true;
  const row = readEngineRow(noteId);
  if (!row || row.pending_ops === null || row.content === text) return true;
  if (refusesLocalEdit(noteId)) return false;
  const s = loadSyncState(noteId);
  if (!s) return true;
  writeSyncState(noteId, localEdit(s, text));
  // After the writer's transaction: the socket reads the stored state.
  queueMicrotask(() => kickLiveNote(noteId));
  return true;
}
